import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";

interface ThreatDetectionConfig {
  isEnabled: boolean;
  alertPhoneNumber: string;
  autoBlockIp: boolean;
  autoEndConversation: boolean;
  sensitivity: number;
  liabilityAcknowledged: boolean;
  liabilityAcknowledgedAt?: string;
  liabilityAcknowledgedBy?: string;
}

const DEFAULT_CONFIG: ThreatDetectionConfig = {
  isEnabled: false,
  alertPhoneNumber: "",
  autoBlockIp: true,
  autoEndConversation: true,
  sensitivity: 2,
  liabilityAcknowledged: false,
};

const LIABILITY_TEXT = `IMPORTANT — PLEASE READ BEFORE ENABLING

Velaro's Threat Detection feature uses automated pattern matching to flag messages that may contain threatening language. This feature is intended as one layer of a broader safety program and has inherent limitations:

• Detection is based on text patterns and may produce false positives (innocent messages flagged) or false negatives (genuine threats missed).
• Velaro makes no warranty, express or implied, that this feature will detect all threatening messages or prevent harm.
• You are solely responsible for your organization's safety protocols, for verifying alerts, and for taking appropriate action.
• This feature is not a substitute for professional security assessment, law enforcement notification, or emergency response procedures.

By enabling this feature, you acknowledge that Velaro bears no liability for undetected threats, incorrectly flagged messages, or any outcomes resulting from reliance on automated threat detection.`;

export default function ThreatDetectionPanel() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const { displayToast } = useToastAlert();

  const hasFeature = subscription?.enableThreatDetection ?? false;
  const alertQuota = subscription?.maxThreatAlertsPerMonth ?? 50;

  const [config, setConfig] = useState<ThreatDetectionConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showLiabilityModal, setShowLiabilityModal] = useState(false);
  const [liabilityChecked, setLiabilityChecked] = useState(false);
  const [pendingEnable, setPendingEnable] = useState(false);

  const load = useCallback(async () => {
    try {
      const resp = await api.messaging.get("ThreatDetection/config");
      if (resp.ok) setConfig(await resp.json());
    } catch {
      // ignore — defaults are fine
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function save(updated: ThreatDetectionConfig) {
    setSaving(true);
    try {
      const resp = await api.messaging.post("ThreatDetection/config", updated);
      if (resp.ok) {
        setConfig(updated);
        displayToast(Severity.Success, "Threat detection settings saved.");
      } else {
        const msg = await resp.text();
        displayToast(Severity.Error, msg || "Failed to save settings.");
      }
    } catch (err) {
      displayToast(Severity.Error, `Save failed: ${String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  function handleToggleEnabled() {
    if (config.isEnabled) {
      save({ ...config, isEnabled: false });
      return;
    }
    if (!config.liabilityAcknowledged) {
      setPendingEnable(true);
      setShowLiabilityModal(true);
    } else {
      save({ ...config, isEnabled: true });
    }
  }

  async function handleLiabilityConfirm() {
    if (!liabilityChecked) return;
    setShowLiabilityModal(false);
    const updated = { ...config, isEnabled: pendingEnable, liabilityAcknowledged: true };
    await save(updated);
    setPendingEnable(false);
    setLiabilityChecked(false);
  }

  if (!hasFeature) {
    return (
      <div className="rounded-lg border border-slate-200 p-6 text-center text-sm text-slate-500">
        <p className="font-medium text-slate-700 mb-1">Threat Detection</p>
        <p>This feature is not included in your current plan. Contact <a href="mailto:sales@velaro.com" className="text-blue-600 underline">sales@velaro.com</a> to add it.</p>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header + status */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Threat Detection</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Monitor incoming messages for threatening language. Sends an SMS alert, and optionally ends the conversation and blocks the visitor's IP.
          </p>
        </div>
        <button
          onClick={handleToggleEnabled}
          disabled={saving}
          className={`shrink-0 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            config.isEnabled
              ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
              : "bg-emerald-600 text-white hover:bg-emerald-700"
          }`}
        >
          {saving ? "Saving…" : config.isEnabled ? "Disable" : "Enable"}
        </button>
      </div>

      <div className={`rounded-lg border p-4 flex items-center gap-3 ${config.isEnabled ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
        <span className={`w-3 h-3 rounded-full shrink-0 ${config.isEnabled ? "bg-emerald-500" : "bg-amber-500"}`} />
        <span className="text-sm font-medium text-slate-700">
          {config.isEnabled ? "Active — monitoring all incoming messages" : "Inactive"}
        </span>
        {config.isEnabled && alertQuota > 0 && (
          <span className="ml-auto text-xs text-slate-500">{alertQuota} SMS alerts/month included</span>
        )}
      </div>

      {/* Alert phone number */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Alert Phone Number</label>
        <p className="text-xs text-slate-500 mb-2">SMS will be sent to this number when a threat is detected. Leave blank to disable SMS alerts (IP blocking and chat ending will still apply).</p>
        <input
          type="tel"
          value={config.alertPhoneNumber}
          onChange={e => setConfig(c => ({ ...c, alertPhoneNumber: e.target.value }))}
          onBlur={() => save(config)}
          placeholder="+12125551234"
          className="w-full max-w-xs rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Sensitivity */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Detection Sensitivity</label>
        <p className="text-xs text-slate-500 mb-2">Higher sensitivity catches more patterns but may produce more false positives.</p>
        <div className="flex gap-3">
          {[
            { value: 1, label: "Low", desc: "Explicit credible threats only" },
            { value: 2, label: "Medium", desc: "Threats with implied harm (recommended)" },
            { value: 3, label: "High", desc: "Any threatening language" },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => save({ ...config, sensitivity: opt.value })}
              className={`flex-1 rounded-lg border p-3 text-left text-sm transition-colors ${
                config.sensitivity === opt.value
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              <div className="font-medium">{opt.label}</div>
              <div className="text-xs mt-0.5 opacity-75">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Automated actions */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">When a threat is detected</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.autoEndConversation}
              onChange={e => save({ ...config, autoEndConversation: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-blue-600"
            />
            <span className="text-sm text-slate-700">Automatically end the conversation</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.autoBlockIp}
              onChange={e => save({ ...config, autoBlockIp: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-blue-600"
            />
            <span className="text-sm text-slate-700">Automatically block the visitor's IP address</span>
          </label>
        </div>
      </div>

      {/* Liability acknowledgment record */}
      {config.liabilityAcknowledged && config.liabilityAcknowledgedAt && (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500">
          Liability disclaimer acknowledged by {config.liabilityAcknowledgedBy ?? "unknown"} on {new Date(config.liabilityAcknowledgedAt).toLocaleString()}.
        </div>
      )}

      {/* Liability modal */}
      {showLiabilityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Liability Acknowledgment Required</h3>
            <div className="text-xs text-slate-600 bg-slate-50 rounded-md p-4 whitespace-pre-wrap max-h-64 overflow-y-auto border border-slate-200">
              {LIABILITY_TEXT}
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={liabilityChecked}
                onChange={e => setLiabilityChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600"
              />
              <span className="text-sm text-slate-700">I have read and understood the above and accept these terms on behalf of my organization.</span>
            </label>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowLiabilityModal(false); setPendingEnable(false); setLiabilityChecked(false); }}
                className="px-4 py-2 rounded-md text-sm text-slate-600 border border-slate-300 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                disabled={!liabilityChecked}
                onClick={handleLiabilityConfirm}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-[var(--color-primary)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Accept & Enable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
